import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { track } from '@/lib/track';
import { Sparkle, Lightning } from '@phosphor-icons/react';
import { LivingBubble } from '@/components/LivingBubble';

const SUGGESTIONS = [
  'curious', 'feral', 'soft', 'restless', 'iridescent',
  'tender', 'electric', 'sleepy', 'reckless', 'glimmering',
];

/**
 * ExpressGenesis — the 30-second skip-the-ritual flow.
 * For returning users who already lived through one Genesis arc
 * and just want another Bubble fast. Single mood-word → personality + art.
 */
const ExpressGenesis = () => {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [vibe, setVibe] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [born, setBorn] = useState(null);
  useEffect(() => { track('creation_started', { source: 'express' }); }, []);

  const submit = async () => {
    if (vibe.trim().length < 2) {
      toast.error('give it a mood word (≥2 chars)');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/genesis/express', {
        vibe_word: vibe.trim(),
        given_name: name.trim() || undefined,
      });
      setBorn(data.bubble);
      refresh?.();
      toast.success(`${data.bubble.name} is breathing`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not breathe');
    } finally { setBusy(false); }
  };

  if (born) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-8 py-16 text-center" data-testid="express-genesis-result">
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}>
          <div className="w-56 h-56 mx-auto">
            <LivingBubble bubble={born} mood="happy" className="w-full h-full" testid="express-born-bubble" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan mt-8">born just now</div>
          <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">{born.name}</h1>
          <p className="text-white/70 italic mt-4 text-sm sm:text-base max-w-md mx-auto">
            "{born.personality?.bio || born.personality}"
          </p>
          <button onClick={() => nav('/home')}
            data-testid="express-genesis-open"
            className="mt-8 btn-volt px-8 py-3 rounded-full text-sm font-bold uppercase tracking-widest">
            meet {born.name}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-16" data-testid="express-genesis-page">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-1.5 chip text-bx-volt mb-3">
          <Lightning size={11} weight="fill" /> express genesis · 30 seconds
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Skip the ritual.
        </h1>
        <p className="text-white/60 mt-3 text-sm sm:text-base">
          Give it a single mood word. A Bubble will breathe itself into being —
          art and all — before you finish reading this sentence.
        </p>
      </div>

      <div className="glass rounded-3xl p-6 sm:p-8 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">mood word</div>
          <input
            value={vibe}
            onChange={(e) => setVibe(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            maxLength={24}
            autoFocus
            data-testid="express-vibe"
            placeholder="curious, feral, soft…"
            className="w-full glass-hi rounded-xl p-4 text-lg outline-none placeholder-white/25" />
          <div className="flex flex-wrap gap-1.5 mt-3">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => setVibe(s)}
                data-testid={`express-suggest-${s}`}
                className="px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 text-[11px] text-white/65">
                {s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">name (optional)</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            maxLength={24}
            data-testid="express-name"
            placeholder="leave blank to let it choose"
            className="w-full glass-hi rounded-xl p-3 text-sm outline-none placeholder-white/25" />
        </div>

        <button onClick={submit} disabled={busy || vibe.trim().length < 2}
          data-testid="express-submit"
          className="w-full btn-volt px-6 py-3 rounded-full text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40">
          {busy ? (
            <><span className="w-3 h-3 rounded-full bg-black/70 breathe" /> breathing it…</>
          ) : (
            <><Sparkle size={14} weight="fill" /> breathe a Bubble</>
          )}
        </button>
      </div>

      <p className="text-[11px] text-white/35 text-center mt-6 italic">
        Want the slow, conversational birth instead?{' '}
        <button onClick={() => nav('/')} className="text-bx-cyan hover:underline">
          take the long way →
        </button>
      </p>
    </div>
  );
};

export default ExpressGenesis;
